import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  LogOut,
  Grid3X3,
  Heart,
  Bookmark,
  UserPlus,
  UserCheck,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useSocialStore } from '@/stores/socialStore';
import { socialService } from '@/services/socialService';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { User, Outfit } from '@/types';

export function ProfileView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { follow, unfollow } = useSocialStore();

  const isOwnProfile = id === currentUser?.id || !id;

  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'outfits' | 'favorites' | 'saved'>('outfits');
  const [outfits] = useState<Outfit[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      setIsLoading(true);
      try {
        if (isOwnProfile && currentUser) {
          setUser(currentUser);
        } else if (id) {
          const profile = await socialService.getProfile(id);
          if (!cancelled) {
            setUser(profile);
          }
        }
      } catch (err) {
        // If profile fetch fails, show basic info
        if (!cancelled) {
          setUser(
            isOwnProfile
              ? currentUser
              : {
                  id: id ?? '',
                  email: '',
                  displayName: 'Unknown User',
                  avatarUrl: null,
                  bio: '',
                  provider: 'email',
                  isPublic: true,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  stats: {
                    garmentCount: 0,
                    outfitCount: 0,
                    followersCount: 0,
                    followingCount: 0,
                    sharedOutfitsCount: 0,
                  },
                },
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [id, isOwnProfile, currentUser]);

  const handleFollowToggle = useCallback(async () => {
    if (!user) return;
    if (isFollowing) {
      await unfollow(user.id);
      setIsFollowing(false);
    } else {
      await follow(user.id);
      setIsFollowing(true);
    }
  }, [user, isFollowing, follow, unfollow]);

  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-vestir-white flex items-center justify-center">
        <LoadingSpinner message="Loading profile..." />
      </div>
    );
  }

  const tabs = [
    { key: 'outfits' as const, icon: Grid3X3, label: 'Outfits' },
    { key: 'favorites' as const, icon: Heart, label: 'Favorites' },
    { key: 'saved' as const, icon: Bookmark, label: 'Saved' },
  ];

  return (
    <motion.div
      className="min-h-screen bg-vestir-white pb-24"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-vestir-white/90 backdrop-blur-xl border-b border-vestir-gray/30">
        <div className="px-4 pt-12 pb-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-vestir-gray/40 rounded-full flex items-center justify-center"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-display text-lg font-bold flex-1 truncate">
            {user.displayName}
          </h1>
          {isOwnProfile && (
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="w-10 h-10 bg-vestir-gray/40 rounded-full flex items-center justify-center text-red-500"
              aria-label="Log out"
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Profile info */}
      <div className="px-4 pt-5 pb-4">
        <div className="flex items-start gap-4">
          <div className="relative">
            <img
              src={user.avatarUrl ?? '/placeholder-avatar.png'}
              alt={user.displayName}
              className="w-20 h-20 rounded-full object-cover ring-3 ring-vestir-purple/20"
            />
          </div>

          <div className="flex-1">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold">{user.stats?.garmentCount ?? 0}</p>
                <p className="text-[10px] text-vestir-gray-dark">Items</p>
              </div>
              <div>
                <p className="text-lg font-bold">
                  {formatStat(user.stats?.followersCount ?? 0)}
                </p>
                <p className="text-[10px] text-vestir-gray-dark">Followers</p>
              </div>
              <div>
                <p className="text-lg font-bold">
                  {formatStat(user.stats?.followingCount ?? 0)}
                </p>
                <p className="text-[10px] text-vestir-gray-dark">Following</p>
              </div>
            </div>
          </div>
        </div>

        {user.bio && (
          <p className="text-sm mt-3">{user.bio}</p>
        )}

        <div className="flex gap-2 mt-4">
          {isOwnProfile ? (
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="flex-1 py-2.5 bg-red-50 text-red-600 rounded-xl text-sm font-medium"
            >
              Log Out
            </button>
          ) : (
            <>
              <button
                onClick={handleFollowToggle}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5
                           transition-colors ${
                             isFollowing
                               ? 'bg-vestir-gray/40 text-vestir-black'
                               : 'bg-vestir-purple text-white'
                           }`}
              >
                {isFollowing ? (
                  <>
                    <UserCheck size={16} /> Following
                  </>
                ) : (
                  <>
                    <UserPlus size={16} /> Follow
                  </>
                )}
              </button>
              <button onClick={() => navigate(`/discover/chat/${user.id}`)} className="py-2.5 px-4 bg-vestir-gray/40 rounded-xl text-sm font-medium">
                Message
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-vestir-gray/30">
        <div className="flex">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3
                           border-b-2 transition-colors ${
                             isActive
                               ? 'border-vestir-purple text-vestir-purple'
                               : 'border-transparent text-vestir-gray-dark'
                           }`}
              >
                <Icon size={16} />
                <span className="text-xs font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      <div className="px-1 pt-1">
        {outfits.length > 0 ? (
          <div className="grid grid-cols-3 gap-0.5">
            {outfits.map((outfit) => (
              <button
                key={outfit.id}
                className="relative aspect-square overflow-hidden"
              >
                <img
                  src={outfit.thumbnailUrl ?? '/placeholder-outfit.png'}
                  alt={outfit.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {outfit.likes > 0 && (
                  <div className="absolute bottom-1 left-1 flex items-center gap-0.5 px-1.5 py-0.5
                                  bg-black/50 rounded-full">
                    <Heart size={10} className="text-white fill-white" />
                    <span className="text-[9px] text-white">{formatStat(outfit.likes)}</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Grid3X3 size={32} className="mx-auto text-vestir-gray-dark/30 mb-3" />
            <p className="text-sm text-vestir-gray-dark">
              {isOwnProfile ? 'Share your first outfit!' : 'No public outfits yet'}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function formatStat(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(num);
}
